import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Plus, Edit, Trash2, Download } from "lucide-react";
import { exportToExcel } from "../lib/excel";
import { API } from "../lib/api";
import { sortByName } from "../lib/sortByName";
import { SearchableSelect } from "../components/SearchableSelect";
import { useConfirm } from "../components/ConfirmDialog";

const Funcoes = () => {
  const [funcoes, setFuncoes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    cliente_id: '',
    status: 'ativo'
  });
  const [editingId, setEditingId] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [confirm, confirmDialog] = useConfirm();
  
  // Estado para filtros
  const [filtros, setFiltros] = useState({
    cliente: 'todos',
    status: 'todos',
    nome: '' // Novo filtro por nome
  });

  const fetchFuncoes = async () => {
    try {
      const [funcoesRes, clientesRes] = await Promise.all([
        axios.get(`${API}/funcoes`),
        axios.get(`${API}/clientes`)
      ]);
      
      // Ordenar funções alfabeticamente por nome
      const funcoesOrdenadas = funcoesRes.data.sort(sortByName);
      setFuncoes(funcoesOrdenadas);
      setClientes(clientesRes.data.filter(c => c.status === 'ativo'));
    } catch (error) {
      toast.error("Erro ao carregar funções");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const carregar = async () => { await fetchFuncoes(); };
    carregar();
  }, []);

  // Função para obter nome do cliente
  const getClienteNome = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    return cliente ? cliente.nome : 'Cliente não encontrado';
  };

  // Função para filtrar funções
  const getFuncoesFiltradas = () => {
    return funcoes.filter(funcao => {
      // Filtro por nome
      if (filtros.nome && !funcao.nome.toLowerCase().includes(filtros.nome.toLowerCase())) {
        return false;
      }
      
      // Filtro por cliente com tratamento para funções sem cliente_id
      if (filtros.cliente !== 'todos') {
        // Se a função não tem cliente_id (undefined/null), ela não deve aparecer
        // quando um cliente específico é selecionado
        if (!funcao.cliente_id) {
          return false;
        }
        
        // Comparação tanto por igualdade estrita quanto por string
        const match = funcao.cliente_id === filtros.cliente || 
                     String(funcao.cliente_id) === String(filtros.cliente);
        if (!match) return false;
      }
      
      // Filtro por status
      if (filtros.status !== 'todos' && funcao.status !== filtros.status) {
        return false;
      }
      
      return true;
    });
  };

  const exportarFuncoesExcel = () => {
    const lista = getFuncoesFiltradas();
    if (lista.length === 0) {
      toast.error("Nenhuma função para exportar");
      return;
    }
    const rows = lista.map(funcao => ({
      'Nome': funcao.nome || '',
      'Descrição': funcao.descricao || '',
      'Cliente': funcao.cliente_id ? getClienteNome(funcao.cliente_id) : '',
      'Status': funcao.status || '',
    }));
    exportToExcel(rows, `funcoes-${new Date().toISOString().split('T')[0]}`, 'Funções');
    toast.success("Funções exportadas para Excel com sucesso!");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/funcoes/${editingId}`, formData);
        toast.success("Função atualizada com sucesso!");
      } else {
        await axios.post(`${API}/funcoes`, formData);
        toast.success("Função criada com sucesso!");
      }
      
      setFormData({
        nome: '',
        descricao: '',
        cliente_id: '',
        status: 'ativo'
      });
      setEditingId(null);
      setIsDialogOpen(false);
      fetchFuncoes();
    } catch (error) {
      toast.error("Erro ao salvar função");
    }
  };

  const handleEdit = (funcao) => {
    setFormData({
      nome: funcao.nome,
      descricao: funcao.descricao || '',
      cliente_id: funcao.cliente_id || '',
      status: funcao.status
    });
    setEditingId(funcao.id);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Excluir função?",
      description: "Tem certeza que deseja deletar esta função? Esta ação não pode ser desfeita.",
      confirmText: "Excluir",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/funcoes/${id}`);
      toast.success("Função deletada com sucesso!");
      fetchFuncoes();
    } catch (error) {
      toast.error("Erro ao deletar função");
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      {confirmDialog}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Funções</h1>
          <p className="text-gray-600 dark:text-gray-300">Gerencie as funções disponíveis</p>
        </div>

        <div className="flex gap-2 no-print">
        <Button variant="outline" onClick={exportarFuncoesExcel} data-testid="exportar-funcoes-excel-btn">
          <Download className="mr-2 h-4 w-4" />
          Exportar Excel
        </Button>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => {
              setFormData({
                nome: '',
                descricao: '',
                cliente_id: '',
                status: 'ativo'
              });
              setEditingId(null);
            }}>
              <Plus className="mr-2 h-4 w-4" />
              Nova Função
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Editar' : 'Nova'} Função</DialogTitle>
              <DialogDescription>
                Preencha os dados da função abaixo.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  placeholder="Ex: Camareira, ASG, Supervisor"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                  placeholder="Descreva as responsabilidades desta função"
                />
              </div>
              
              <div>
                <Label htmlFor="cliente_id">Cliente</Label>
                <SearchableSelect
                  options={[
                    { value: '', label: 'Selecione um cliente' },
                    ...clientes
                      .sort(sortByName)
                      .map(cliente => ({
                        value: cliente.id,
                        label: cliente.nome
                      }))
                  ]}
                  value={formData.cliente_id}
                  onValueChange={(value) => setFormData({...formData, cliente_id: value})}
                  placeholder="Selecione um cliente"
                  searchPlaceholder="Buscar cliente..."
                  emptyText="Nenhum cliente encontrado"
                  className="w-full"
                />
              </div>
              
              <div>
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="inativo">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit">
                  {editingId ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>

      </div>

      {/* Filtros */}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <Label htmlFor="filtro-nome">Buscar por Nome</Label>
              <Input
                id="filtro-nome"
                type="text"
                placeholder="Digite o nome da função..."
                value={filtros.nome}
                onChange={(e) => setFiltros({...filtros, nome: e.target.value})}
              />
            </div>
            
            <div>
              <Label htmlFor="filtro-cliente">Cliente</Label>
              <SearchableSelect
                options={[
                  { value: 'todos', label: 'Todos os clientes' },
                  ...clientes
                    .sort(sortByName)
                    .map(cliente => ({
                      value: cliente.id,
                      label: cliente.nome
                    }))
                ]}
                value={filtros.cliente}
                onValueChange={(value) => setFiltros({...filtros, cliente: value})}
                placeholder="Todos os clientes"
                searchPlaceholder="Buscar cliente..."
                emptyText="Nenhum cliente encontrado"
                className="w-full"
              />
            </div>
            
            <div>
              <Label htmlFor="filtro-status">Status</Label>
              <Select 
                value={filtros.status} 
                onValueChange={(value) => setFiltros({...filtros, status: value})}
              >
                <SelectTrigger id="filtro-status">
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="inativo">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Button 
                variant="outline" 
                onClick={() => setFiltros({ cliente: 'todos', status: 'todos', nome: '' })}
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getFuncoesFiltradas().map((funcao) => (
                <TableRow key={funcao.id}>
                  <TableCell className="font-medium">{funcao.nome}</TableCell>
                  <TableCell>{funcao.descricao || '-'}</TableCell>
                  <TableCell>{funcao.cliente_id ? getClienteNome(funcao.cliente_id) : '-'}</TableCell>
                  <TableCell>
                    <Badge variant={funcao.status === 'ativo' ? 'default' : 'secondary'}>
                      {funcao.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(funcao)} data-testid={`editar-funcao-${funcao.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(funcao.id)} data-testid={`excluir-funcao-${funcao.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Funcoes;
